import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, Plus, Trash, LockKey, PenNib, Users } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const EMPTY_OCCUPANT = { nombre: "", apellidos: "", documento: "", nacimiento: "", nacionalidad: "", direccion: "" };

const SignaturePad = ({ onChange }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const drawing = useRef(false);
  const [strokes, setStrokes] = useState(0);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const resize = () => {
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
      ctx.scale(2, 2);
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#1A1D1A";
    };
    resize();
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ref.current.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = ref.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const end = () => {
    if (drawing.current) {
      drawing.current = false;
      setStrokes((s) => s + 1);
      onChange(ref.current.toDataURL("image/png"));
    }
  };
  const clear = () => {
    const canvas = ref.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setStrokes(0);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        data-testid="signature-canvas"
        className="w-full h-40 bg-white border border-line touch-none cursor-crosshair"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <div className="flex justify-between items-center mt-2">
        <p className="text-[11px] text-ink/50">{strokes > 0 ? t("checkin.signReady") : t("checkin.signHint")}</p>
        <button type="button" data-testid="signature-clear" onClick={clear} className="text-[11px] uppercase tracking-[0.15em] text-terracotta hover:underline">
          {t("checkin.signClear")}
        </button>
      </div>
    </div>
  );
};

export default function Checkin() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [occupants, setOccupants] = useState([{ ...EMPTY_OCCUPANT }]);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);

  const load = () =>
    api.get(`/checkin/${bookingId}`).then((r) => {
      setInfo(r.data);
      if (r.data.lang && r.data.lang !== i18n.language) i18n.changeLanguage(r.data.lang);
    }).catch(() => setError(true));

  useEffect(() => {
    load().then(() => {
      // Vuelta de Stripe tras bloquear la fianza
      const sid = params.get("session_id");
      if (params.get("deposito") === "ok" && sid) {
        api.get(`/checkin/${bookingId}/deposit-status`, { params: { session_id: sid } })
          .then((r) => {
            if (r.data.deposit_status === "autorizada") {
              toast.success(t("checkin.depositOk"));
              load();
            }
          })
          .catch(() => {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const setOcc = (i, k, v) => {
    const next = [...occupants];
    next[i] = { ...next[i], [k]: v };
    setOccupants(next);
  };

  const submitCheckin = async () => {
    const valid = occupants.filter((o) => o.nombre.trim() && o.apellidos.trim() && o.documento.trim());
    if (!valid.length) return toast.error(t("checkin.errMissing"));
    if (!signature) return toast.error(t("checkin.errSignature"));
    setSaving(true);
    try {
      await api.post(`/checkin/${bookingId}`, { occupants: valid, signature });
      toast.success(t("checkin.okSubmitted"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("checkin.errSubmit"));
    }
    setSaving(false);
  };

  const payDeposit = async () => {
    setDepositLoading(true);
    try {
      const { data } = await api.post(`/checkin/${bookingId}/deposit`, null, { params: { origin_url: window.location.origin } });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("checkin.errDeposit"));
      setDepositLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-sand flex flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-serif text-3xl text-ink">{t("checkin.notFound")}</p>
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-terracotta">{t("checkin.backHome")}</Link>
      </div>
    );
  }
  if (!info) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <span className="text-xs uppercase tracking-[0.3em] text-ink/40">…</span>
      </div>
    );
  }

  const allDone = info.checkin_completed && info.deposit_status === "autorizada";
  const perWeek = Math.round(info.deposit_amount / (info.weeks || 1));

  return (
    <div data-testid="checkin-page" className="min-h-screen bg-sand py-14 px-6">
      <div className="max-w-[680px] mx-auto">
        <div className="border-b border-line pb-8 mb-8">
          <p className="font-serif text-4xl text-ink">Luz de <span className="italic text-[#C8A97E]">Cádiz</span></p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-terracotta mt-2">{t("checkin.kicker")}</p>
        </div>

        {allDone ? (
          <div data-testid="checkin-done" className="bg-white border border-line p-8 text-center">
            <Check size={36} className="mx-auto text-[#4A6B53]" />
            <p className="font-serif text-2xl text-ink mt-4">{t("checkin.doneTitle")}</p>
            <p className="text-sm text-ink/60 mt-2">{t("checkin.doneText", { date: info.checkin })}</p>
          </div>
        ) : (
          <>
            <div className="bg-white border border-line p-6 mb-6">
              <p className="font-serif text-2xl text-ink">{info.apartment_name}</p>
              <p className="text-sm text-ink/60 mt-1">{t("checkin.summary", { checkin: info.checkin, checkout: info.checkout, nights: info.nights, guests: info.guests })}</p>
            </div>

            {/* 1. Ocupantes */}
            <div className="bg-white border border-line p-6 mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-1 flex items-center gap-2"><Users size={15} /> {t("checkin.step1")}</p>
              <p className="text-[11px] text-ink/50 mb-5">{t("checkin.step1Note")}</p>
              {occupants.map((o, i) => (
                <div key={i} data-testid={`occupant-${i}`} className="border border-line p-4 mb-4 relative">
                  {occupants.length > 1 && (
                    <button type="button" data-testid={`occupant-remove-${i}`} onClick={() => setOccupants(occupants.filter((_, j) => j !== i))} className="absolute top-3 right-3 text-ink/40 hover:text-terracotta" aria-label={t("checkin.remove")}>
                      <Trash size={15} />
                    </button>
                  )}
                  <p className="text-[10px] uppercase tracking-[0.2em] text-ink/45 mb-3">{t("checkin.occupant", { n: i + 1 })}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid={`occ-nombre-${i}`} className="input-line" placeholder={t("checkin.firstName")} value={o.nombre} onChange={(e) => setOcc(i, "nombre", e.target.value)} />
                    <input data-testid={`occ-apellidos-${i}`} className="input-line" placeholder={t("checkin.lastName")} value={o.apellidos} onChange={(e) => setOcc(i, "apellidos", e.target.value)} />
                    <input data-testid={`occ-documento-${i}`} className="input-line" placeholder={t("checkin.docId")} value={o.documento} onChange={(e) => setOcc(i, "documento", e.target.value)} />
                    <input data-testid={`occ-nacimiento-${i}`} type="date" className="input-line" placeholder={t("checkin.birthdate")} value={o.nacimiento} onChange={(e) => setOcc(i, "nacimiento", e.target.value)} />
                    <input data-testid={`occ-nacionalidad-${i}`} className="input-line" placeholder={t("checkin.nationality")} value={o.nacionalidad} onChange={(e) => setOcc(i, "nacionalidad", e.target.value)} />
                    <input data-testid={`occ-direccion-${i}`} className="input-line" placeholder={t("checkin.address")} value={o.direccion} onChange={(e) => setOcc(i, "direccion", e.target.value)} />
                  </div>
                </div>
              ))}
              <button type="button" data-testid="occupant-add" onClick={() => setOccupants([...occupants, { ...EMPTY_OCCUPANT }])} className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-terracotta hover:text-atlantic transition-colors">
                <Plus size={14} /> {t("checkin.addOccupant")}
              </button>
            </div>

            {/* 2. Firma del estado de la casa */}
            <div className="bg-white border border-line p-6 mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-1 flex items-center gap-2"><PenNib size={15} /> {t("checkin.step2")}</p>
              <p className="text-[11px] leading-relaxed text-ink/55 mb-4">
                {t("checkin.stateIntro")}
                <strong> {t("checkin.stateDeadline")}</strong>
                {t("checkin.stateRest")}
              </p>
              <SignaturePad onChange={setSignature} />
            </div>

            {/* 3. Fianza */}
            <div className="bg-white border border-line p-6 mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-1 flex items-center gap-2"><LockKey size={15} /> {t("checkin.step3", { amount: info.deposit_amount })}</p>
              <p className="text-[11px] leading-relaxed text-ink/55 mb-4">
                {t("checkin.depositText1", { perWeek })}<strong>{t("checkin.depositBold")}</strong>{t("checkin.depositText2")}
              </p>
              {info.deposit_status === "autorizada" ? (
                <p data-testid="deposit-ok" className="flex items-center gap-2 text-sm text-[#4A6B53]"><Check size={16} /> {t("checkin.depositOk")}</p>
              ) : (
                <button data-testid="deposit-btn" onClick={payDeposit} disabled={depositLoading} className="w-full py-4 bg-ink text-sand text-xs uppercase tracking-[0.2em] hover:bg-terracotta transition-colors disabled:opacity-60">
                  {depositLoading ? t("checkin.depositRedirect") : t("checkin.depositBtn", { amount: info.deposit_amount })}
                </button>
              )}
            </div>

            <button
              data-testid="checkin-submit"
              onClick={submitCheckin}
              disabled={saving || info.checkin_completed}
              className="w-full py-4 bg-terracotta text-sand text-xs uppercase tracking-[0.2em] hover:bg-atlantic transition-colors disabled:opacity-60"
            >
              {info.checkin_completed ? t("checkin.submitted") : saving ? t("checkin.submitting") : t("checkin.submit")}
            </button>
            <p className="text-center text-[11px] text-ink/45 mt-4">{t("checkin.help")}</p>
          </>
        )}
      </div>
    </div>
  );
}
